// frontend/src/components/formularios/FormularioRegistroLubricantes.jsx
// FORMULARIO UNIFICADO PARA REGISTROS DE LUBRICANTES (ACEITES Y ENGRASES)
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import FormularioBase from './FormularioBase';
import apiClient from '../../services/apiClient';
import { fetchCentroCostoPreferidoDesdeEquipo } from '../../utils/centroCosto';
import { hoyArgentina } from '../../utils/dateUtils';

// `resolverOpciones` y `guardar` permiten reusar este mismo formulario desde la
// app movil, que lee los catalogos de la copia local y encola el envio cuando no
// hay senal. Sin ellos se comporta exactamente como siempre.
// `valoresIniciales` precarga el formulario SIN ponerlo en modo edición. Lo usa
// la app móvil para reabrir una carga que el servidor rechazó al sincronizar:
// el registro nunca llegó a existir del otro lado, así que sigue siendo un alta
// —tiene que salir por POST— pero con lo que la persona ya había tipeado en la
// obra. Pasarlo por `registro` haría que el formulario intentara un PATCH sobre
// un id inexistente.
const FormularioRegistroLubricantes = ({ registro = null, valoresIniciales = null, initialEquipo = null, initialMecanico = null, showTitle = true, noNestedScroll = false, compact = false, fullWidth = true, onSave: parentOnSave, onCancel: parentOnCancel, hideFkCreateButtons = false, enableSaveAndNew = false, resolverOpciones, guardar, botonesMovil = false, permitirEscanerQR = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id || registro);

  const [initialData, setInitialData] = useState({});

  // Campos del formulario unificado basados en el modelo RegistroLubricantes
  // Incluye TODOS los campos
  const fields = [
    // Campos comunes y obligatorios
    {
      name: 'equipo', label: 'Equipo', type: 'select', optionsEndpoint: 'equipos/', required: true, searchable: true, searchRemote: true, preserveOnDuplicate: true,
      // Botón de escaneo QR junto al buscador — sólo en la app móvil, que es
      // donde se carga parada frente al equipo (ver `permitirEscanerQR`).
      ...(permitirEscanerQR ? { qrEscaner: true } : {}),
    },
    { name: 'fecha', label: 'Fecha', type: 'date', required: true },
    {
      name: 'tarea', label: 'Tarea', type: 'select', optionsEndpoint: 'tareasengrase/', required: true,
      fkConfig: { apiEndpoint: 'tareasengrase', titulo: 'Tareas de Engrase', descripcion: 'Tipos de tareas de lubricación y engrase', icon: 'bi bi-tools' },
      preserveOnDuplicate: true,
    },

    // Campos de lubricante (para recargas). Solo repuestos marcados para uso en
    // mantenimiento diario (ver checkbox en el alta de Repuesto) — acota el catálogo
    // a los fluidos reales del camión lubricador, no todo el almacén.
    {
      name: 'lubricante', label: 'Fluido de Mantenimiento', type: 'select', optionsEndpoint: 'repuestos/?uso_mantenimiento_diario=true', required: false,
      fkConfig: { apiEndpoint: 'repuestos', titulo: 'Repuestos', descripcion: 'Tildá "Uso en mantenimiento diario" para que aparezca acá', icon: 'bi bi-droplet-fill' },
    },
    { name: 'cantidad', label: 'Cantidad (Litros)', type: 'number', step: '0.01', required: false },
    {
      name: 'centro_costo', label: 'Centro de Costo', type: 'select', optionsEndpoint: 'centroscosto/', required: false,
      fkConfig: { apiEndpoint: 'centroscosto', titulo: 'Centros de Costo', descripcion: 'Centros de costo disponibles', icon: 'bi bi-building' },
      preserveOnDuplicate: true,
    },

    // Campos de personal (para engrases)
    {
      name: 'mecanico', label: 'Mecánico', type: 'select', optionsEndpoint: 'mecanicos/', required: false,
      fkConfig: { apiEndpoint: 'mecanicos', titulo: 'Mecánicos', descripcion: 'Mecánicos disponibles', icon: 'bi bi-person-gear' },
      preserveOnDuplicate: true,
    },
    { name: 'chofer', label: 'Chofer', type: 'text', required: false, preserveOnDuplicate: true },

    // Campo común
    { name: 'observaciones', label: 'Observaciones', type: 'textarea', required: false },
  ];

  // Si se pasó el prop `registro` (por ejemplo al abrir desde la lista en un modal), usarlo.
  // En caso contrario, si estamos en modo edición por URL, cargar desde la API.
  useEffect(() => {
    if (registro) {
      const d = registro;
      setInitialData({
        id: d.id,
        equipo: d.equipo?.id || d.equipo || '',
        fecha: d.fecha || '',
        lubricante: d.lubricante?.id || d.lubricante || '',
        cantidad: d.cantidad || '',
        centro_costo: d.centro_costo?.id || d.centro_costo || '',
        tarea: d.tarea?.id || d.tarea || '',
        mecanico: d.mecanico?.id || d.mecanico || '',
        chofer: d.chofer || '',
        observaciones: d.observaciones || ''
      });
      return;
    }

    if (!isEditing) {
      setInitialData({
        fecha: hoyArgentina(),
        ...(initialEquipo ? { equipo: String(initialEquipo) } : {}),
        ...(initialMecanico ? { mecanico: String(initialMecanico) } : {}),
        // Corrección de una carga rechazada: se precarga lo ya tipeado, pero
        // sin `id`, para que siga siendo un alta.
        ...(valoresIniciales ? {
          equipo: valoresIniciales.equipo ?? '',
          fecha: valoresIniciales.fecha || hoyArgentina(),
          lubricante: valoresIniciales.lubricante ?? '',
          cantidad: valoresIniciales.cantidad ?? '',
          centro_costo: valoresIniciales.centro_costo ?? '',
          tarea: valoresIniciales.tarea ?? '',
          mecanico: valoresIniciales.mecanico ?? '',
          chofer: valoresIniciales.chofer || '',
          observaciones: valoresIniciales.observaciones || '',
        } : {}),
      });
      return;
    }

    const load = async () => {
      try {
        const resp = await apiClient.get(`lubricantes/${id}/`);
        const d = resp.data || {};
        setInitialData({
          id: d.id,
          equipo: d.equipo?.id || d.equipo || '',
          fecha: d.fecha || '',
          lubricante: d.lubricante?.id || d.lubricante || '',
          cantidad: d.cantidad || '',
          centro_costo: d.centro_costo?.id || d.centro_costo || '',
          tarea: d.tarea?.id || d.tarea || '',
          mecanico: d.mecanico?.id || d.mecanico || '',
          chofer: d.chofer || '',
          observaciones: d.observaciones || ''
        });
      } catch (error) {
        console.error('Error cargando registro de lubricante:', error);
      }
    };

    load();
  }, [registro, valoresIniciales, id, isEditing, initialEquipo, initialMecanico]);

  useEffect(() => {
    const equipoId = initialData?.equipo;
    if (!equipoId || initialData?.centro_costo) return;

    let cancelled = false;
    const hydrateCentroCosto = async () => {
      try {
        const centroCosto = await fetchCentroCostoPreferidoDesdeEquipo(apiClient, equipoId);
        if (!cancelled && centroCosto) {
          setInitialData((prev) => {
            if (!prev?.equipo || String(prev.equipo) !== String(equipoId) || prev?.centro_costo) return prev;
            return { ...prev, centro_costo: centroCosto };
          });
        }
      } catch (error) {
        console.warn('No se pudo autocompletar el centro de costo desde el equipo', error);
      }
    };

    hydrateCentroCosto();
    return () => { cancelled = true; };
  }, [initialData?.equipo, initialData?.centro_costo]);

  // Reglas de validación (equipo, fecha y tarea obligatorios)
  const validationRules = {
    equipo: { required: true },
    fecha: { required: true },
    tarea: { required: true }
  };

  // Manejar guardado exitoso
  const handleSave = (savedData, opts = {}) => {
    if (opts.keepOpen) return; // "Guardar y nuevo/duplicar": el formulario se queda abierto
    console.log('Registro de lubricante guardado:', savedData);
    // Volver a la lista de lubricantes
    navigate('/lubricantes');
  };

  // Manejar cancelación
  const handleCancel = () => {
    navigate('/lubricantes');
  };

  const handleFieldChange = async (name, value) => {
    if (name !== 'equipo') return undefined;
    if (!value) return { centro_costo: '' };

    try {
      return { centro_costo: await fetchCentroCostoPreferidoDesdeEquipo(apiClient, value) };
    } catch (error) {
      console.warn('No se pudo sincronizar el centro de costo con el equipo', error);
      return { centro_costo: '' };
    }
  };

  return (
    <FormularioBase
      title={isEditing ? 'Editar Registro de Lubricante' : 'Nuevo Registro de Lubricante'}
      icon="bi bi-droplet-fill"
      fields={fields}
      initialData={initialData}
      apiEndpoint="lubricantes/"
      isEditing={isEditing}
      validationRules={validationRules}
      onSave={parentOnSave || handleSave}
      onCancel={parentOnCancel || handleCancel}
      showTitle={showTitle}
      noNestedScroll={noNestedScroll}
      compact={compact}
      fullWidth={fullWidth}
      onFieldChange={handleFieldChange}
      hideFkCreateButtons={hideFkCreateButtons}
      enableSaveAndNew={enableSaveAndNew}
      resolverOpciones={resolverOpciones}
      guardar={guardar}
      botonesMovil={botonesMovil}
    />
  );
};

export default FormularioRegistroLubricantes;
